import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Wand2, Globe, AlertCircle } from 'lucide-react';
import { Button } from '../../../shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../shared/components/ui/card';

const DOMAIN_REGEX = /^[a-z0-9][a-z0-9.-]{0,252}\.[a-z]{2,}$/i;
const FAVICON_DEBOUNCE_MS = 400;

const cleanDomain = (raw) => {
  if (!raw) return '';
  let s = raw.trim().toLowerCase();
  s = s.replace(/^https?:\/\//, '').replace(/\/$/, '').split('/')[0];
  return s;
};

const faviconUrl = (domain) => {
  if (!domain || !domain.includes('.')) return null;
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`;
};

export const BannerForm = ({ onGenerate, isLoading, hasResult }) => {
  const [companyDomain, setCompanyDomain] = useState('');
  const [error, setError] = useState(null);
  const [currentFavicon, setCurrentFavicon] = useState(null);
  const [faviconBroken, setFaviconBroken] = useState(false);
  const debounceRef = useRef(null);

  // Debounced favicon update as the user types; instant on blur.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const cleaned = cleanDomain(companyDomain);
    const url = faviconUrl(cleaned);
    if (!url) {
      setCurrentFavicon(null);
      setFaviconBroken(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setCurrentFavicon(url);
      setFaviconBroken(false);
    }, FAVICON_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [companyDomain]);

  const handleBlur = () => {
    // Skip the debounce — snap to the current value immediately.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const cleaned = cleanDomain(companyDomain);
    const url = faviconUrl(cleaned);
    if (url) {
      setCurrentFavicon(url);
      setFaviconBroken(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleaned = cleanDomain(companyDomain);
    if (!cleaned) {
      setError("Please enter your company's domain.");
      return;
    }
    if (!DOMAIN_REGEX.test(cleaned)) {
      setError("That doesn't look like a valid domain. Example: growleads.io");
      return;
    }
    setError(null);
    onGenerate({ companyDomain: cleaned });
  };

  if (hasResult) return null;

  return (
    <section id="generator" className="py-12 md:py-20">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Card className="shadow-xl border-border/50">
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-display text-2xl md:text-3xl text-foreground">
                Generate your LinkedIn banner
              </CardTitle>
              <CardDescription className="text-base">
                Paste your company's domain. We'll read your site and design a banner that feels on-brand — tagline, colors, and all.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <label htmlFor="companyDomain" className="text-sm font-medium text-foreground">
                    Company Domain
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-5 pointer-events-none">
                      {currentFavicon && !faviconBroken ? (
                        <img
                          src={currentFavicon}
                          alt=""
                          className="w-5 h-5 rounded-sm"
                          onError={() => setFaviconBroken(true)}
                        />
                      ) : (
                        <Globe className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                    <input
                      id="companyDomain"
                      type="text"
                      inputMode="url"
                      autoComplete="off"
                      autoFocus
                      value={companyDomain}
                      onChange={(e) => { setCompanyDomain(e.target.value); if (error) setError(null); }}
                      onBlur={handleBlur}
                      placeholder="growleads.io"
                      disabled={isLoading}
                      className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-background text-foreground text-base
                        hover:border-primary/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20
                        disabled:opacity-50 disabled:cursor-not-allowed transition-colors placeholder:text-muted-foreground"
                    />
                  </div>
                  {error && (
                    <div className="flex items-start gap-2 text-sm text-destructive mt-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Your domain is all we need. No other fields, no sign-up for AI assets.
                  </p>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isLoading}
                  className="w-full text-base font-medium gap-2"
                >
                  <Wand2 className="w-4 h-4" />
                  {isLoading ? 'Designing your banner…' : 'Generate banner'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default BannerForm;
